import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useLocalizedNavigation } from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import breakpoints from '@repo/ui-components/breakpoints';
import styles from '@repo/ui-components/styles';
import IconSearch from '@repo/ui-components/svgs/search-icon.svg';

const SearchFormContainer = styled.form<{ disabled?: boolean }>`
  position: relative;
  display: flex;
  flex: 1;
  width: 100%;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  @media (min-width: ${breakpoints.lg}) {
    margin-bottom: 0;
  }
`;

const SearchInputField = styled.input`
  overflow: hidden;
  flex: 1;
  padding: ${styles.spacing['spacing-md']} ${styles.spacing['spacing-sm']};
  opacity: 1;
  font-weight: inherit;
  position: relative;
  padding-left: ${styles.spacing['spacing-1xl']};
  appearance: none;
  border-radius: 0;
  font-size: ${styles.fontSize.base};

  &:read-only {
    background-color: inherit;
    color: ${({ theme }) => theme.colors.searchInput};
  }

  svg {
    position: absolute;
    left: 0;
  }
`;

const Icon = styled(IconSearch)`
  position: absolute;
  left: 0.75rem;
  height: 1rem;
  top: 33%;
`;

interface SearchFormProps {
  currentRefinement?: string | null;
  refine?: (query: string) => void;
  disabled?: boolean;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  searchResultsContainerRef?: React.RefObject<HTMLElement>;
  ariaExpanded?: boolean;
}

const SearchForm: React.FC<SearchFormProps> = ({
  currentRefinement = null,
  refine = () => {},
  disabled = false,
  onSubmit: onSubmitProp,
  onKeyDown = () => {},
  searchResultsContainerRef,
  ariaExpanded,
}) => {
  const navigate = useLocalizedNavigation();
  const { tt } = useUiComponentsInternationalContext();

  const defaultOnSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      navigate(`/search`);
    },
    [navigate]
  );

  const onSubmit = onSubmitProp ?? defaultOnSubmit;

  return (
    <SearchFormContainer
      onSubmit={onSubmit}
      noValidate
      role="search"
      action="."
      disabled={disabled}
    >
      <SearchInputField
        value={currentRefinement}
        type="search"
        onChange={event => refine(event.currentTarget.value)}
        placeholder={tt(s => s.headerCommon.searchPlaceholder)}
        disabled={disabled}
        role="combobox"
        aria-owns={searchResultsContainerRef?.current?.id}
        aria-controls={searchResultsContainerRef?.current?.id}
        aria-label="Search"
        onKeyDown={onKeyDown}
        aria-expanded={ariaExpanded}
        // if the search results are expanded, the results container will be focused by the screen reader, but the DOM focus will still be the input
        aria-activedescendant={
          ariaExpanded ? searchResultsContainerRef?.current?.id : undefined
        }
        className="border border-input bg-input-background text-input-foreground placeholder-input-placeholder"
      />

      <Icon className="fill-input-foreground" aria-hidden />
    </SearchFormContainer>
  );
};

export default SearchForm;
